import { useEffect, ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthContext'

interface LayoutProps {
  children: ReactNode
  showHeader?: boolean
  showBottomNav?: boolean
  title?: string
  showBackButton?: boolean
}

function Layout({ 
  children, 
  showHeader = true, 
  showBottomNav = true,
  title,
  showBackButton = false
}: LayoutProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme')
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const shouldBeDark = savedTheme === 'dark' || (!savedTheme && prefersDark) || savedTheme === null
    
    if (shouldBeDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [])

  const isHome = location.pathname === '/'
  const isHistory = location.pathname === '/history'
  const isSettings = location.pathname === '/settings'

  return (
    <div className="min-h-screen flex flex-col bg-background-light dark:bg-background-dark text-slate-900 dark:text-white overflow-x-hidden selection:bg-primary/30">
      {/* Top App Bar */}
      {showHeader && (
        <header className="sticky top-0 z-50 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 transition-colors duration-300">
          <div className="flex items-center justify-between p-4 pb-3 max-w-md mx-auto w-full">
            {showBackButton ? (
              <button 
                onClick={() => navigate(-1)}
                className="flex items-center justify-center rounded-full h-10 w-10 hover:bg-slate-200 dark:hover:bg-surface-dark transition-colors"
              >
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
            ) : (
              <Link to="/" className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded bg-primary text-white">
                  <span className="material-symbols-outlined text-[20px]">terminal</span>
                </div>
                <h2 className="text-slate-900 dark:text-white text-lg font-bold leading-tight tracking-[-0.015em]">
                  AllinOneDecoder
                </h2>
              </Link>
            )}
            
            {title && showBackButton && (
              <h1 className="text-slate-900 dark:text-white text-lg font-bold leading-tight tracking-[-0.015em] flex-1 text-center pr-10">
                {title}
              </h1>
            )}

            {!showBackButton && (
              <div className="flex items-center justify-end">
                <Link
                  to="/auth"
                  className="flex items-center justify-center rounded-lg h-10 w-10 bg-transparent text-slate-700 dark:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-surface-dark transition-colors"
                  aria-label={user ? t('auth.titleAccount') : t('auth.login')}
                  title={user ? t('auth.titleAccount') : t('auth.login')}
                >
                  <span className={`material-symbols-outlined ${user ? 'filled' : ''}`}>person</span>
                </Link>
                <Link 
                  to="/pricing"
                  className="flex items-center justify-center rounded-lg h-10 w-10 bg-transparent text-primary hover:bg-primary/10 transition-colors"
                >
                  <span className="material-symbols-outlined filled">crown</span>
                </Link>
              </div>
            )}
          </div>
        </header>
      )}

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col w-full max-w-md mx-auto pb-24">
        {children}
      </main>

      {/* Bottom Navigation */}
      {showBottomNav && (
        <nav className="bottom-nav pb-safe">
          <div className="flex items-center justify-around h-16 max-w-md mx-auto">
            <Link 
              to="/" 
              className={`bottom-nav-item ${isHome ? 'active' : ''}`}
            >
              <span className={`material-symbols-outlined ${isHome ? 'filled' : ''}`}>home</span>
              <span className="text-[10px] font-medium">{t('nav.home')}</span>
            </Link>
            <Link 
              to="/history" 
              className={`bottom-nav-item ${isHistory ? 'active' : ''}`}
            >
              <span className="material-symbols-outlined">history</span>
              <span className="text-[10px] font-medium">{t('nav.history')}</span>
            </Link>
            <Link 
              to="/settings" 
              className={`bottom-nav-item ${isSettings ? 'active' : ''}`}
            >
              <span className="material-symbols-outlined">settings</span>
              <span className="text-[10px] font-medium">{t('nav.settings')}</span>
            </Link>
          </div>
        </nav>
      )}
    </div>
  )
}

export default Layout
